import { jsPDF } from "jspdf";
import { formatMoney, formatMoneyPrecise } from "./formatMoney";

export interface ExpenseMetricItem {
  primary: string;
  secondary?: string;
  /** Kwota — pomijana dla pozycji bez wartości (np. teczki inwestycji). */
  amount?: number;
  status?: string;
}

export interface ExpenseBudgetRow {
  label: string;
  planned: number;
  engaged: number;
  settled: number;
  remaining: number;
}

export interface ExpenseMetricInput {
  title: string;
  divisionSymbol: string;
  divisionName: string;
  chapterSymbol: string;
  chapterName: string;
  plannedAmount: number;
  engagedAmount: number;
  settledAmount: number;
  /** Rozbicie budżetu zadania na paragrafy (ten sam zestaw przekrojów). */
  paragraphs: ExpenseBudgetRow[];
  engagements: ExpenseMetricItem[];
  invoices: ExpenseMetricItem[];
  contracts: ExpenseMetricItem[];
  tenders: ExpenseMetricItem[];
  investmentCases: ExpenseMetricItem[];
}

let fontRegularB64: string | null = null;
let fontBoldB64: string | null = null;

async function loadFontB64(url: string): Promise<string> {
  const res = await fetch(url);
  const buf = await res.arrayBuffer();
  const bytes = new Uint8Array(buf);
  let binary = "";
  const chunk = 8192;
  for (let i = 0; i < bytes.length; i += chunk) {
    binary += String.fromCharCode(...bytes.subarray(i, i + chunk));
  }
  return btoa(binary);
}

export async function generateExpenseMetricPdf(
  data: ExpenseMetricInput,
): Promise<Blob> {
  if (!fontRegularB64)
    fontRegularB64 = await loadFontB64("/fonts/LiberationSans-Regular.ttf");
  if (!fontBoldB64)
    fontBoldB64 = await loadFontB64("/fonts/LiberationSans-Bold.ttf");

  const doc = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" });
  doc.addFileToVFS("LiberationSans-Regular.ttf", fontRegularB64);
  doc.addFont("LiberationSans-Regular.ttf", "LiberationSans", "normal");
  doc.addFileToVFS("LiberationSans-Bold.ttf", fontBoldB64);
  doc.addFont("LiberationSans-Bold.ttf", "LiberationSans", "bold");

  const L = 14;
  const R = 196;
  const W = 182;
  let y = 16;

  const setF = (size: number, bold = false, r = 28, g = 27, b = 31) => {
    doc.setFont("LiberationSans", bold ? "bold" : "normal");
    doc.setFontSize(size);
    doc.setTextColor(r, g, b);
  };

  const hline = () => {
    doc.setLineWidth(0.35);
    doc.setDrawColor(93, 73, 199);
    doc.line(L, y, R, y);
  };

  // ── Nagłówek dokumentu ─────────────────────────────────────────────
  setF(13, true, 93, 73, 199);
  doc.text("METRYKA WYDATKU BUDŻETOWEGO", L, y);
  y += 4;
  hline();
  y += 7;

  // ── Tytuł ──────────────────────────────────────────────────────────
  setF(8, true, 100, 90, 120);
  doc.text("TYTUŁ:", L, y);
  setF(9.5, true, 28, 27, 31);
  const titleLines = doc.splitTextToSize(data.title || "—", W - 22);
  doc.text(titleLines, L + 22, y);
  y += titleLines.length * 5 + 3;

  const col1x = L;
  const labelW = 30;

  const remaining = (data.plannedAmount || 0) - (data.engagedAmount || 0);

  setF(7.5, true, 120, 110, 140);
  doc.text("DZIAŁ:", col1x, y);
  setF(9, false, 28, 27, 31);
  doc.text(
    `${data.divisionSymbol}${data.divisionName ? "  —  " + data.divisionName : ""}`,
    col1x + labelW,
    y,
  );
  y += 5.5;
  setF(7.5, true, 120, 110, 140);
  doc.text("ROZDZIAŁ:", col1x, y);
  setF(9, false, 28, 27, 31);
  doc.text(
    `${data.chapterSymbol}${data.chapterName ? "  —  " + data.chapterName : ""}`,
    col1x + labelW,
    y,
  );
  y += 6;

  // ── Budżet zadania i stan wykonania (Razem + paragrafy) ────────────
  setF(10, true, 93, 73, 199);
  doc.text("BUDŻET ZADANIA I STAN WYKONANIA", L, y);
  y += 6;

  // Prawe krawędzie kolumn (cała tabela wyrównana do prawej).
  const tcols = [50, 86, 122, 158, R];
  const drawBudgetRow = (
    cells: string[],
    opts: { header?: boolean; bold?: boolean } = {},
  ) => {
    if (opts.header) setF(7.5, true, 120, 110, 140);
    else setF(8.5, opts.bold ?? false, 28, 27, 31);
    cells.forEach((c, i) => doc.text(c, tcols[i], y, { align: "right" }));
    y += 5;
  };

  drawBudgetRow(
    ["", "Budżet zadania", "Zaangażowane", "Rozliczone", "Pozostało"],
    { header: true },
  );
  doc.setLineWidth(0.2);
  doc.setDrawColor(205, 200, 220);
  doc.line(L, y - 3, R, y - 3);

  drawBudgetRow(
    [
      "Razem",
      formatMoneyPrecise(data.plannedAmount),
      formatMoneyPrecise(data.engagedAmount),
      formatMoneyPrecise(data.settledAmount),
      formatMoneyPrecise(remaining),
    ],
    { bold: true },
  );

  (data.paragraphs ?? []).forEach((p) => {
    if (y > 270) {
      doc.addPage();
      y = 16;
    }
    drawBudgetRow([
      p.label,
      formatMoneyPrecise(p.planned),
      formatMoneyPrecise(p.engaged),
      formatMoneyPrecise(p.settled),
      formatMoneyPrecise(p.remaining),
    ]);
  });

  y += 3;
  hline();
  y += 7;

  // ── Listy pozycji: Zaangażowania / Faktury / Umowy ─────────────────
  const drawItemSection = (label: string, items: ExpenseMetricItem[]) => {
    if (y > 255) {
      doc.addPage();
      y = 16;
    }
    setF(10, true, 93, 73, 199);
    doc.text(label, L, y);
    y += 6;

    if (items.length === 0) {
      setF(9, false, 120, 110, 140);
      doc.text("Brak pozycji.", L, y);
      y += 7;
      return;
    }

    items.forEach((it) => {
      if (y > 270) {
        doc.addPage();
        y = 16;
      }
      const amountStr = it.amount != null ? formatMoney(it.amount) : "";
      setF(9, false, 28, 27, 31);
      const amountW = amountStr ? doc.getTextWidth(amountStr) : 0;
      setF(9, true, 28, 27, 31);
      const primary = doc.splitTextToSize(it.primary || "—", W - amountW - 8)[0];
      doc.text(primary, L + 3, y);
      if (amountStr) {
        setF(9, false, 28, 27, 31);
        doc.text(amountStr, R, y, { align: "right" });
      }
      y += 4.5;

      const sub = [it.secondary, it.status].filter(Boolean).join("  ·  ");
      if (sub) {
        setF(8, false, 120, 110, 140);
        const subLine = doc.splitTextToSize(sub, W - 6)[0];
        doc.text(subLine, L + 3, y);
        y += 4;
      }
      y += 1.5;
    });
    y += 2;
  };

  drawItemSection("ZAANGAŻOWANIA", data.engagements ?? []);
  drawItemSection("FAKTURY", data.invoices ?? []);
  drawItemSection("UMOWY", data.contracts ?? []);
  drawItemSection("POSTĘPOWANIA PRZETARGOWE", data.tenders ?? []);
  drawItemSection("TECZKI INWESTYCJI", data.investmentCases ?? []);

  if (y > 262) {
    doc.addPage();
    y = 16;
  }
  hline();
  y += 8;

  // ── Stopka ─────────────────────────────────────────────────────────
  setF(7.5, false, 130, 120, 140);
  doc.text(`Data wydruku: ${new Date().toLocaleDateString("pl-PL")}`, L, y);
  doc.text(".......................................", R, y, { align: "right" });
  y += 4.5;
  setF(7, false, 170, 165, 180);
  doc.text("podpis i pieczęć", R, y, { align: "right" });

  return doc.output("blob");
}
